/**
 * DEVELOPER DOCUMENTATION
 *
 * Include your custom JavaScript here.
 *
 * The theme Focal has been developed to be easily extensible through the usage of a lot of different JavaScript
 * events, as well as the usage of custom elements (https://developers.google.com/web/fundamentals/web-components/customelements)
 * to easily extend the theme and re-use the theme infrastructure for your own code.
 *
 * The technical documentation is summarized here.
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A VARIANT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a the user has changed the variant in a selector. The target get you the form
 * that triggered this event.
 *
 * Example:
 *
 * document.addEventListener('variant:changed', function(event) {
 *   let variant = event.detail.variant; // Gives you access to the whole variant details
 *   let form = event.target;
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * MANUALLY CHANGE A VARIANT
 * ------------------------------------------------------------------------------------------------------------
 *
 * You may want to manually change the variant, and let the theme automatically adjust all the selectors. To do
 * that, you can get the DOM element of type "<product-variants>", and call the selectVariant method on it with
 * the variant ID.
 *
 * Example:
 *
 * const productVariantElement = document.querySelector('product-variants');
 * productVariantElement.selectVariant(12345);
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A NEW VARIANT IS ADDED TO THE CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a variant is added to the cart through a form selector (product page, quick
 * view...). This event DOES NOT include any change done through the cart on an existing variant. For that,
 * please refer to the "cart:updated" event.
 *
 * Example:
 *
 * document.addEventListener('variant:added', function(event) {
 *   var variant = event.detail.variant; // Get the variant that was added
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN THE CART CONTENT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever the cart content has changed (if the quantity of a variant has changed, if a variant
 * has been removed, if the note has changed...). This event will also be emitted when a new variant has been
 * added (so you will receive both "variant:added" and "cart:updated"). Contrary to the variant:added event,
 * this event will give you the complete details of the cart.
 *
 * Example:
 *
 * document.addEventListener('cart:updated', function(event) {
 *   var cart = event.detail.cart; // Get the updated content of the cart
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * REFRESH THE CART/MINI-CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * If you are adding variants to the cart and would like to instruct the theme to re-render the cart, you cart
 * send the cart:refresh event, as shown below:
 *
 * document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
 *   bubbles: true
 * }));
 *
 * ------------------------------------------------------------------------------------------------------------
 * USAGE OF CUSTOM ELEMENTS
 * ------------------------------------------------------------------------------------------------------------
 *
 * Our theme makes extensive use of HTML custom elements. Custom elements are an awesome way to extend HTML
 * by creating new elements that carry their own JavaScript for adding new behavior. The theme uses a large
 * number of custom elements, but the two most useful are drawer and popover. Each of those components add
 * a "open" attribute that you can toggle on and off. For instance, let's say you would like to open the cart
 * drawer, whose id is "mini-cart", you simply need to retrieve it and set its "open" attribute to true (or
 * false to close it):
 *
 * document.getElementById('mini-cart').open = true;
 *
 * Thanks to the power of custom elements, the theme will take care automagically of trapping focus, maintaining
 * proper accessibility attributes...
 *
 * If you would like to create your own drawer, you can re-use the <drawer-content> content. Here is a simple
 * example:
 *
 * // Make sure you add "aria-controls", "aria-expanded" and "is" HTML attributes to your button:
 * <button type="button" is="toggle-button" aria-controls="id-of-drawer" aria-expanded="false">Open drawer</button>
 *
 * <drawer-content id="id-of-drawer">
 *   Your content
 * </drawer-content>
 *
 * The nice thing with custom elements is that you do not actually need to instantiate JavaScript yourself: this
 * is done automatically as soon as the element is inserted to the DOM.
 *
 * ------------------------------------------------------------------------------------------------------------
 * THEME DEPENDENCIES
 * ------------------------------------------------------------------------------------------------------------
 *
 * While the theme tries to keep outside dependencies as small as possible, the theme still uses third-party code
 * to power some of its features. Here is the list of all dependencies:
 *
 * "vendor.js":
 *
 * The vendor.js contains required dependencies. This file is loaded in parallel of the theme file.
 *
 * - custom-elements polyfill (used for built-in elements on Safari - v1.0.0): https://github.com/ungap/custom-elements
 * - web-animations-polyfill (used for polyfilling WebAnimations on Safari 12, this polyfill will be removed in 1 year - v2.3.2): https://github.com/web-animations/web-animations-js
 * - instant-page (v5.1.0): https://github.com/instantpage/instant.page
 * - tocca (v2.0.9); https://github.com/GianlucaGuarini/Tocca.js/
 * - seamless-scroll-polyfill (v2.0.0): https://github.com/magic-akari/seamless-scroll-polyfill
 *
 * "flickity.js": v2.2.0 (with the "fade" package). Flickity is only loaded on demand if there is a product image
 * carousel on the page. Otherwise it is not loaded.
 *
 * "photoswipe": v4.1.3. PhotoSwipe is only loaded on demand to power the zoom feature on product page. If the zoom
 * feature is disabled, then this script is never loaded.
 */


/*
* ------------------------------------------------------------------------------------------------------------
*  UNIT CONVERSION FOR PDP SPECIFICATION TABLES
* ------------------------------------------------------------------------------------------------------------
*
* Converts values in table cells between either mm and inches or cm and feet
*
*/

document.addEventListener('DOMContentLoaded', function () {

    // Toggle for mm/in conversion
    const mmInToggle = document.getElementById('toggle-mm-in');
    if (mmInToggle) {
        mmInToggle.addEventListener('change', function (event) {
            const isMillimeters = event.target.value === 'MM';
            document.querySelectorAll('#table-mm-in td.mm').forEach(function (element) {
                if (isMillimeters) {
                    let cellValue = element.textContent.replace(/"/, '');
                    let ogValue = parseFloat(cellValue, 10);
                    let newValue = Math.round(ogValue * 25.4);
                    element.textContent = newValue;
                } else {
                    let cellValue = element.textContent.replace(/"/, '');
                    let ogValue = parseFloat(cellValue, 10);
                    let newValue = (ogValue / 25.4).toFixed(2);
                    element.textContent = newValue + '"';
                }
            });
        });
    }

    // Toggle for cm/ft conversion
    const cmFtToggle = document.getElementById('toggle-cm-ft');
    if (cmFtToggle) {
        cmFtToggle.addEventListener('change', function (event) {
            const isCentimeters = event.target.value === 'CM';

            document.querySelectorAll('#table-cm-ft td.cm').forEach((element) => {
                const minElem = element.querySelector('.size_cm_min');
                const maxElem = element.querySelector('.size_cm_max');

                const convertToInches = (cm) => {
                    const totalInches = cm * 0.393701;
                    const feet = Math.floor(totalInches / 12);
                    const inches = Math.round(totalInches % 12);
                    return `${feet}'${inches}"`;
                };

                if (!isCentimeters) {
                    const minCm = parseFloat(minElem.getAttribute('original-data'));
                    const maxCm = parseFloat(maxElem.getAttribute('original-data'));
                    minElem.textContent = convertToInches(minCm);
                    maxElem.textContent = convertToInches(maxCm);
                } else {
                    minElem.textContent = minElem.getAttribute('original-data');
                    maxElem.textContent = maxElem.getAttribute('original-data');
                }
            });
        });
    }
});


/*
* ------------------------------------------------------------------------------------------------------------
*  MINI CART ADD-ONS
* ------------------------------------------------------------------------------------------------------------
*
* The following code adds an event listener to the mini-cart build removal buttons
* It initiates the loading spinner
* Then removes all build items from the cart with the same build id
* Finally refreshes the mini cart
*
*/

const miniCart = document.getElementById('mini-cart');

miniCart.addEventListener('click', async (e) => {
    // See if a remove‐build button (any of them) was clicked
    const btn = e.target.closest('.remove-build-button');
    if (!btn) return;

    e.preventDefault();
    const buildId = btn.dataset.buildId;
    if (!buildId) return;

    // Find the <line-item> wrapper for that button
    const lineItemEl = btn.closest('line-item');
    if (lineItemEl) {
        lineItemEl.dispatchEvent(new CustomEvent('line-item-quantity:change:start', { bubbles: true }));
    }

    try {
        // Fetch the cart from Shopify Cart API
        const cart = await fetch('/cart.js', {
            headers: { 'Accept': 'application/json' }
        }).then(r => r.json());

        // Collect all the line keys we want to zero out
        const params = new URLSearchParams();

        cart.items.forEach(li => {
            if (li.properties._custom_builder_id == buildId ||
                li.properties._boldBuilderId == buildId) {
                params.append(`updates[${li.key}]`, 0);
            }
        });

        if (!params.toString()) {
            console.warn('No build items found to remove.');
            return;
        }

        // Update the cart with Shopify Cart API
        const response = await fetch(`${window.Shopify.routes.root}cart/update.js`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/x-www-form-urlencoded',
                'Accept': 'application/json'
            },
            body: params.toString()
        });

        // Shopify returns 200 + the updated cart JSON
        const updatedCart = await response.json();
        if (!response.ok) throw new Error(updatedCart.description || 'Unknown error');

        // Refresh the mini cart
        document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));

        // Tell the line-item loader we’re done
        if (lineItemEl) {
            lineItemEl.dispatchEvent(new CustomEvent('line-item-quantity:change:end', {
                bubbles: true,
                detail: {
                    cart: updatedCart,
                    newLineQuantity: 0
                }
            }));
        }

    } catch (err) {
        console.error('Failed to remove build:', err);
    }
});



/*
* ------------------------------------------------------------------------------------------------------------
*  PRE-ORDER ADD ONS
* ------------------------------------------------------------------------------------------------------------
*
* The following code adds an event listener to the product form for variant changed
* It removes any hidden inputs containing pre-order properties if they exist
* Then adds them back if variant has pre-order or availability message metafields
*
*/
document.addEventListener('variant:changed', function (event) {
    let variant = event.detail.variant;
    let form = event.target;

    if (!variant || !form) return;

    let productForm = form.closest('.product-form') || document;

    // Grab metafield data for all variants
    let variantMetafields = JSON.parse(
        productForm.querySelector('script[data-variant-metafields]')?.textContent || '{}'
    );

    let currentVariantMetafields = variantMetafields[String(variant.id)] || {};

    let preOrder = currentVariantMetafields.pre_order;
    let availabilityMessage = currentVariantMetafields.availability_message;

    // Remove current hidden inputs if they exist
    form.querySelector("input[name='properties[_pre_order]']")?.remove();
    form.querySelector("input[name='properties[_availability_message]']")?.remove();

    // Add pre-order hidden input if variant is pre-order
    if (preOrder) {
        let preOrderInput = document.createElement('input');
        preOrderInput.type = 'hidden';
        preOrderInput.name = 'properties[_pre_order]';
        preOrderInput.value = 'true';
        form.appendChild(preOrderInput);
    }

    // Add availability message hidden input if variant has one
    if (availabilityMessage) {
        let availabilityMessageInput = document.createElement('input');
        availabilityMessageInput.type = 'hidden';
        availabilityMessageInput.name = 'properties[_availability_message]';
        availabilityMessageInput.value = availabilityMessage;
        form.appendChild(availabilityMessageInput);
    }
});